import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { env } from "@/lib/env/env";
import { attachAdminHttpAuthInterceptor } from "@/lib/api/admin-http-auth";

/**
 * Admin HTTP client.
 *
 * Auth model (TBD with backend):
 *   - Preferred: httpOnly cookie set on /admin/auth/login. `withCredentials`
 *     is enabled so the cookie is sent automatically.
 *   - Token in JS memory only if the backend opts for bearer tokens — never
 *     localStorage / sessionStorage (XSS would lift it instantly).
 *
 * 401 responses attempt a single silent refresh (see admin-http-auth.ts) before
 * ending the session. TODO(observability): forward x-request-id to Sentry.
 */

function generateRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function createAdminHttpClient(): AxiosInstance {
  const client = axios.create({
    baseURL: env.VITE_ADMIN_API_BASE_URL,
    withCredentials: true,
    timeout: 30_000,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.headers.set("x-request-id", generateRequestId());
    config.headers.set("x-admin-client", "zone-admin-dashboard");

    // A multipart upload must NOT inherit the JSON default above. Axios only picks the
    // right Content-Type (with the generated boundary) when the header is absent — an
    // explicit value wins, so the body arrives with no boundary and the server rejects
    // it as malformed. That is what made the WF-76 evidence upload return 422 while
    // looking, from the browser, like a perfectly ordinary request.
    //
    // Deleting it here rather than at the call site so the next person who adds an
    // upload does not have to know this.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      config.headers.delete("Content-Type");
    }
    return config;
  });

  attachAdminHttpAuthInterceptor(client);

  return client;
}

export const adminHttp = createAdminHttpClient();
